import React, { useState } from 'react'
import { removeKeywordSuffix } from 'containers/VerticalPage/common/utils'
import columns, { ColumnTitleWithTooltip } from '../../UI/VerticalTable/columns'
import { COLUMNS_IDS } from 'UI/VerticalTable/utils'
import { ITableColumnsType } from 'uikit/tables/default'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import CloseIcon from '@mui/icons-material/Close'

const isEthAddress = (s: string) => /^0x[a-fA-F0-9]{40}$/.test(s)

const truncateEth = (addr: string) => `${addr.slice(0, 6)}…${addr.slice(-4)}`

const CopyBtn: React.FC<{ text: string }> = ({ text }) => {
  const [copied, setCopied] = useState(false)
  return (
    <Tooltip title={copied ? 'Copied!' : 'Copy'} placement='top'>
      <IconButton
        size='small'
        onClick={e => {
          e.stopPropagation()
          navigator.clipboard.writeText(text)
          setCopied(true)
          setTimeout(() => setCopied(false), 1500)
        }}
        sx={{ ml: 0.5, p: '2px', opacity: 0.6, '&:hover': { opacity: 1 } }}
      >
        <ContentCopyIcon sx={{ fontSize: 14 }} />
      </IconButton>
    </Tooltip>
  )
}

const EthAddressesModal: React.FC<{ names: string[]; open: boolean; onClose: () => void }> = ({
  names,
  open,
  onClose,
}) => (
  <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
    <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      Online Names
      <IconButton onClick={onClose} size='small'>
        <CloseIcon />
      </IconButton>
    </DialogTitle>
    <DialogContent dividers>
      <Stack spacing={1}>
        {names.map((name, i) => (
          <Stack key={i} direction='row' alignItems='center' spacing={1} sx={{ py: 0.5 }}>
            <Typography
              variant='body2'
              sx={{
                fontFamily: isEthAddress(name) ? 'monospace' : 'inherit',
                color: isEthAddress(name) ? '#1890ff' : 'inherit',
                wordBreak: 'break-all',
              }}
            >
              {name}
            </Typography>
            <CopyBtn text={name} />
          </Stack>
        ))}
      </Stack>
    </DialogContent>
  </Dialog>
)

const ProfileNamesCell: React.FC<{ item: any }> = ({ item }) => {
  const [modalOpen, setModalOpen] = useState(false)

  const profileNames: string[] =
    item?.revenueSources?.reduce((acc: string[], { profileName }: { profileName: string }) => {
      if (!acc.includes(profileName)) acc.push(profileName)
      return acc
    }, [] as string[]) || []

  if (profileNames.length === 0) return <></>

  const first = profileNames[0]
  const displayFirst = isEthAddress(first) ? truncateEth(first) : first
  const isEth = isEthAddress(first)

  if (profileNames.length === 1) {
    return (
      <Stack direction='row' alignItems='center'>
        <Typography
          variant='body2'
          sx={{
            color: isEth ? '#1890ff' : 'inherit',
            fontFamily: isEth ? 'monospace' : 'inherit',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {displayFirst}
        </Typography>
        {isEth && <CopyBtn text={first} />}
      </Stack>
    )
  }

  return (
    <>
      <Stack
        direction='row'
        alignItems='center'
        spacing={0.5}
        sx={{ cursor: 'pointer' }}
        onClick={e => {
          e.stopPropagation()
          setModalOpen(true)
        }}
      >
        <Typography
          variant='body2'
          sx={{
            color: isEth ? '#1890ff' : 'inherit',
            fontFamily: isEth ? 'monospace' : 'inherit',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {displayFirst}
        </Typography>
        <Typography variant='body2' sx={{ color: '#1890ff', flexShrink: 0 }}>
          {` +${profileNames.length - 1}`}
        </Typography>
      </Stack>
      <EthAddressesModal names={profileNames} open={modalOpen} onClose={() => setModalOpen(false)} />
    </>
  )
}

export const cryptoColumns: ITableColumnsType<any> = [
  {
    ...columns[COLUMNS_IDS.profileNames],
    title: <ColumnTitleWithTooltip title='Online Name' tooltipText='Names used by entity/individual online' />,
    render: (item: any) => <ProfileNamesCell item={item} />,
  },
  { ...columns[COLUMNS_IDS.verifiedName], minWidth: 150 },
  columns[COLUMNS_IDS.tin],
  columns[COLUMNS_IDS.reportStatus],
  columns[COLUMNS_IDS.unreportedRevenue],
  columns[COLUMNS_IDS.reported],
  columns[COLUMNS_IDS.totalInflowsUsd],
  columns[COLUMNS_IDS.totalOutflowsUsd],
  columns[COLUMNS_IDS.maxAnnualGain],
  columns[COLUMNS_IDS.totalNFTMintsUsd],
  columns[COLUMNS_IDS.totalMintRevenue],
  columns[COLUMNS_IDS.totalRevenue],
  columns[COLUMNS_IDS.totalRoyalties],
  columns[COLUMNS_IDS.dataSources],
  columns[COLUMNS_IDS.activityType],
  columns[COLUMNS_IDS.firstActivity],
  columns[COLUMNS_IDS.caseId],
  columns[COLUMNS_IDS.locations],
]

export const keysForEmptyColumnsRequest = removeKeywordSuffix(
  cryptoColumns.map(el => `${el.key}` || '').filter(key => Boolean(key)),
)
